import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { MCButton } from 'src/components/basic';
import { TableLibrary } from 'src/components/complex';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';

import {
  addressObject,
  lang,
  number,
  permissions,
  remCss,
  scssVariables,
  upperFirstCharAll,
  withRouter
} from 'src/helpers';

import model from './model';
import ShortInfo from '../../../components/user/ShortInfo';

const listTotal = [
  { title: 'Patients', name: 'patient', icon: 'people-icon' },
  { title: 'Appointments', name: 'appointment', icon: 'weekly-sale-icon' },
  { title: 'Treatments', name: 'treatment', icon: 'hydrotherapy-icon' }
];

class Stats extends BaseComponent {
  componentDidMount() {
    Object.values(model).map((m) => m.subscribe(this.subscription));
  }

  componentWillUnmount() {
    Object.values(model).map((m) => m.unsubscribe(this.subscription));
  }

  render() {
    const totals = {
      patient: model.modelPatient.getUser().patientCount || 0,
      appointment: model.modelAppointment.getUser().appointmentCount || 0,
      treatment: model.modelTreatment.getUser().treatmentCount || 0
    };

    return (
      <div className={'mc-container-content mc-stats'} style={{ marginTop: remCss(20) }}>
        <div className={'mc-flex mc-box-shadow-group'}>
          {listTotal.map((item) => (
            <div data-testid='stats-card' className={'mc-box-shadow mc-grow'} key={item.name}>
              <div className={'mc-box-shadow-wrapped'}>
                <div className={'mc-flex'}>
                  <SVGIcon iconname={item.icon} />
                  <div className={'mc-flex-col mc-grow'}>
                    <div className={'mc-note'}>{lang.t('Total')}</div>
                    <div className={'mc-h2'}>{lang.t(item.title)}</div>
                  </div>
                  <div className={'mc-h1'}>{number.format(totals[item.name])}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={'mc-flex mc-box-shadow-group'}>
          <div className={'mc-box-shadow mc-grow'}>
            <div className={'mc-box-shadow-wrapped'}>
              <div className={'mc-flex'} style={{ marginBottom: remCss(30) }}>
                <div className={'mc-h3 mc-grow'}>{lang.t('Patients')}</div>
                <MCButton
                  light
                  small
                  primary
                  radius
                  type={'link'}
                  to={permissions.PAGES.PATIENTS_PAGE.path}
                >
                  {lang.t('View All')}
                </MCButton>
              </div>
              <TableLibrary
                showPagination={false}
                columns={[
                  {
                    label: 'Patients',
                    width: '30%',
                    renderCell: (item) => <ShortInfo user={item} />
                  },
                  {
                    label: 'Diseases',
                    width: '35%',
                    renderCell: (item) => item.disease
                  },
                  {
                    label: 'Location',
                    width: '35%',
                    renderCell: (item) => item.countryAddress && addressObject(item).fullAddressName
                  }
                ]}
                data={model.modelPatient.items()}
              />
            </div>
          </div>
          <div className={'mc-box-shadow mc-grow'}>
            <div className={'mc-box-shadow-wrapped'}>
              <div className={'mc-flex'} style={{ marginBottom: remCss(30) }}>
                <div className={'mc-h3 mc-grow'}>{lang.t('Appointments')}</div>
                <MCButton
                  light
                  small
                  primary
                  radius
                  type={'link'}
                  to={permissions.PAGES.APPOINTMENTS_PAGE.path}
                >
                  {lang.t('View All')}
                </MCButton>
              </div>
              <TableLibrary
                showPagination={false}
                columns={[
                  {
                    label: 'Patients',
                    width: '30%',
                    renderCell: (item) => <ShortInfo user={item.partner} />
                  },
                  {
                    label: 'Diseases',
                    width: '30%',
                    renderCell: (item) => item.data?.disease
                  },
                  {
                    label: 'Date',
                    width: '20%',
                    renderCell: (item) =>
                      item.data?.startDate ? moment(item.data.startDate).format('DD MMM YYYY') : ''
                  },
                  {
                    label: 'Time',
                    width: '20%',
                    cellStyles: { textAlign: 'end' },
                    renderCell: (item) =>
                      item.data?.startDate ? moment(item.data.startDate).format('hh:mm A') : ''
                  }
                ]}
                data={model.modelAppointment.items()}
              />
            </div>
          </div>
        </div>

        <div className={'mc-flex mc-box-shadow-group'}>
          <div className={'mc-box-shadow mc-grow'}>
            <div className={'mc-box-shadow-wrapped'}>
              <div className={'mc-flex'} style={{ marginBottom: remCss(30) }}>
                <div className={'mc-h3 mc-grow'}>{lang.t('Treatments')}</div>
                <MCButton
                  light
                  small
                  primary
                  radius
                  to={'/'}
                  type={'link'}
                  state={{
                    tab: 'treatment'
                  }}
                >
                  {lang.t('View All')}
                </MCButton>
              </div>
              <TableLibrary
                showPagination={false}
                columns={[
                  {
                    label: 'Diseases',
                    width: '30%',
                    renderCell: (item) => item.data?.diagnosis
                  },
                  {
                    label: 'Patients',
                    width: '30%',
                    cellStyles: { width: '100%' },
                    renderCell: (item) => <ShortInfo user={item.partner} />
                  },
                  {
                    label: 'Date',
                    width: '20%',
                    renderCell: (item) => (item.date ? moment(item.date).format('DD-MMM-YYYY') : '')
                  },
                  {
                    label: 'Status',
                    width: '10%',
                    cellStyles: { maxWidth: 150 },
                    renderCell: (item) =>
                      item.data.feedback?.status && (
                        <button className={'mc-status-' + item.data.feedback?.status}>
                          {lang.t(upperFirstCharAll(item.data.feedback?.status.replace('_', ' ')))}
                        </button>
                      )
                  },
                  {
                    label: 'Actions',
                    width: '10%',
                    cellStyles: { textAlign: 'center' },
                    renderCell: (item) => (
                      <Link
                        to={permissions.PAGES.TREATMENTS_DETAIL_PAGE.match(item.id)}
                        state={{ state: { treatment: item } }}
                        className={'mc-no-style-btn'}
                        style={{ margin: 'auto' }}
                      >
                        <SVGIcon
                          iconname={BASE_ICONS.EYE_FILL}
                          fill={scssVariables['colors-mc-primary']}
                          fillOpacity={'0.5'}
                          width={42}
                          dark
                          padding={5}
                          radius
                        />
                      </Link>
                    )
                  }
                ]}
                data={model.modelTreatment.items()}
              />
            </div>
          </div>
          <div
            className={'mc-box-shadow mc-grow'}
            style={{ minWidth: remCss(400), maxWidth: '40%' }}
          >
            <div className={'mc-box-shadow-wrapped'}>
              <div className={'mc-flex'} style={{ marginBottom: remCss(30) }}>
                <div className={'mc-h3 mc-grow'}>{lang.t('Messages')}</div>
                <MCButton
                  light
                  small
                  primary
                  radius
                  type={'link'}
                  to={permissions.PAGES.MESSAGES_PAGE.path}
                >
                  {lang.t('View All')}
                </MCButton>
              </div>
              <TableLibrary
                showPagination={false}
                columns={[
                  {
                    width: '70%',
                    renderCell: (item) => <ShortInfo user={item.partner} description={item.text} />
                  },
                  {
                    width: '30%',
                    cellStyles: { textAlign: 'right' },
                    renderCell: (item) => moment(item.date).format('hh:mm A')
                  }
                ]}
                data={model.modelMessage.items()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Stats);
